// ChatWorkspace — the chat column + right panel (MCP App / site preview), extracted VERBATIM from the
// former RosApp render body (lines 736–1040). This is the "chat must not regress" boundary: same JSX,
// same callbacks, same refs. It is ALWAYS MOUNTED by AppShell (react-router toggles visibility, never
// unmounts) because R3Chat owns its own session/message/streaming state internally — unmounting it
// would destroy the live conversation. All state lives in AppShell and is passed down as props.

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import Radio from '@mui/material/Radio'
import Button from '@mui/material/Button'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Alert from '@mui/material/Alert'
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar'
import BugReportIcon from '@mui/icons-material/BugReport'
import DownloadIcon from '@mui/icons-material/Download'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'
import CodeIcon from '@mui/icons-material/Code'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser'
import HistoryIcon from '@mui/icons-material/History'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloseIcon from '@mui/icons-material/Close'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import React, { useState, useEffect, useCallback, useMemo, useRef, useSyncExternalStore } from 'react'
import R3Chat from '@reachinsight-app/shared/src/components/R3Chat/R3Chat'
import type { R3ChatHandle } from '@reachinsight-app/shared/src/components/R3Chat/R3Chat'
import McpAppContainer from '@reachinsight-app/shared/src/components/R3Chat/McpAppContainer'
import type { McpAppContainerHandle } from '@reachinsight-app/shared/src/components/R3Chat/McpAppContainer'
import type { AppArtifactBuilderData, AppArtifactOpenData, LocationRequestData, MarkdownArtifactPanelData, McpAppData, SiteArtifacts, WebsiteChangeSetReadyData } from '@reachinsight-app/shared/src/components/R3Chat/types'
import { WebChatService } from '@reachinsight-app/shared/src/services/WebChatService'
import { useFeatureFlag } from '@reachinsight-app/shared/src'
import { ArtifactProtectControl } from './ArtifactProtectControl'
import { ArtifactsPanel } from './ArtifactsPanel'
import { ArtifactPreviewPanel } from './ArtifactPreviewPanel'
import { ChatHeader } from './ChatHeader'
import { useRosShell } from '../RosShellContext'
import type { SessionArtifact, PanelArtifactVersion } from './rosArtifacts'
import { artifactLabel } from './rosArtifacts'
import ArticleIcon from '@mui/icons-material/Article'
import DescriptionIcon from '@mui/icons-material/Description'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import { exportConversation, type ExportFormat, type ConversationMessage } from '../utils/chatExport'
import { MicroAppDock } from './MicroAppDock'
import {
  ContextualWebsiteComposer,
  ChangeReviewPanel,
  RevisionHistoryPanel,
  TargetChipBar,
  WebsiteBuilderService as RevisionWebsiteBuilderService,
  WebsiteComposerDraftStore,
  WebsitePreviewFrame,
  toWebsiteEditContext,
  type PendingWebsiteTarget,
  type WebsiteAnchorRect,
  type WebsiteApplyMode,
  type WebsiteChangeSetSummary,
  type WebsitePreviewBridgeMessage,
} from './website-builder'
import { hasEditableWebsiteSnapshot } from './website-builder/editingAvailability'
import { WebsiteBuilderService } from '../services/WebsiteBuilderService'
import {MarkdownArtifactPanel, MarkdownArtifactService} from './markdown-artifact'
import {useRosFeatures} from '../access/useRosFeatures'
import {fetchAppArtifactBuilderSkillZip} from '../services/AppArtifactBuilderSkillService'
import {PublishedWebsiteFeedback} from './PublishedWebsiteFeedback'

interface AgentModelRow {
  slug: string
  modelProvider: string
  modelId: string
}

interface CatalogModelRow {
  id: string
  label: string
  spec: string
  provider: string
  modelId: string
  vendor: string
  openWeight: boolean
  available: boolean
  supportsVision: boolean
}

type SelectComposerPlacement = 'canvas' | 'ros' | 'both'

function stablePublishedUrl(value: string | undefined): string | null {
  if (!value) return null
  try {
    const url = new URL(value)
    if (/\/s\/[^/]+\/v\d+\/?$/.test(url.pathname)) url.pathname = url.pathname.replace(/\/v\d+\/?$/, '')
    url.search = ''
    url.hash = ''
    return url.toString().replace(/\/$/, '')
  } catch {
    return value
  }
}
import { useChatMessageChangeHandler } from '../hooks/useChatMessageMirroring'

export interface ChatWorkspaceProps {
  // chat
  chatRef: React.RefObject<R3ChatHandle>
  chatService: WebChatService
  userName?: string
  liveSessionId: string | undefined
  apiBaseUrl: string
  token: string | null
  devMode: boolean
  setDevMode: (v: boolean) => void
  /** Whether to show the dev-mode toggle icon at all (gated by the ros-dev-mode flag). */
  showDevToggle: boolean
  /** Report message presence up to AppShell (drives the rail's "Ask Ros" highlight). */
  onHasMessagesChange: (hasMessages: boolean) => void
  /** Mirror of the displayed conversation up to AppShell — lets the quiet re-sync (cross-tab /
   *  refocus) compare fetched history against what's on screen and SKIP the disruptive
   *  loadSession rebuild when nothing changed. */
  onMessagesMirror: (msgs: ConversationMessage[]) => void
  /** True from the moment a message is SENT until the turn settles — the quiet re-sync gates on
   *  this so it can never cancel/rebuild over this tab's own live turn. */
  onStreamingChange: (isStreaming: boolean) => void
  /** True while a session's history is being fetched + applied — shows the loading overlay so the
   *  user never sees R3Chat's empty state masquerading as a lost conversation. */
  sessionResuming: boolean
  // chat callbacks
  onSessionCreated: (sessionId: string) => void
  fetchVoiceToken: () => Promise<string>
  uploadFile: (sessionId: string, file: File, signal?: AbortSignal) => Promise<string>
  onStreamingAssistantContent: (content: string | null) => void
  onMcpApp: (data: McpAppData) => void
  onMarkdownArtifact: (data: MarkdownArtifactPanelData) => void
  onAppArtifactOpen: (data: AppArtifactOpenData) => void
  onMicroAppOpen?: (definitionId: string, displayName?: string) => void
  onLocationRequest?: (data: LocationRequestData) => void
  onSiteBuilderPlan: (data: import('@reachinsight-app/shared/src/components/R3Chat/types').SiteBuilderPlanData) => void
  onSiteBuilderGenerating: (siteId: string | number) => void
  onSiteBuilderCodeChunk: (data: import('@reachinsight-app/shared/src/components/R3Chat/types').SiteBuilderCodeChunkData) => void
  onSiteBuilderPreview: (renderHtml: string, siteId: string | number, revision?: number, htmlContent?: string) => void
  onSiteBuilderPublished: (url: string, siteId: string | number) => void

  // draft banner
  draftBanner: { type: string; baseVersion: number | null } | null
  setDraftBanner: (v: null) => void

  // panels
  mcpPanelData: McpAppData | null
  mcpPanelOpen: boolean
  setMcpPanelOpen: (v: boolean) => void
  mcpContainerRef: React.RefObject<McpAppContainerHandle>
  siteArtifacts: SiteArtifacts | null
  siteBuilderOpen: boolean
  setSiteBuilderOpen: (v: boolean) => void
  markdownArtifact: MarkdownArtifactPanelData | null
  markdownArtifactOpen: boolean
  setMarkdownArtifactOpen: (v: boolean) => void
  setMarkdownArtifact: (v: MarkdownArtifactPanelData | null) => void
  artifactsMgmtEnabled: boolean
  artifactsListOpen: boolean
  setArtifactsListOpen: (v: boolean) => void
  previewArtifact: SessionArtifact | null
  setPreviewArtifact: (v: SessionArtifact | null) => void
  downloadArtifactVersion: (artifact: SessionArtifact, version: PanelArtifactVersion) => void
  onDeleteArtifact: (artifact: SessionArtifact) => void
  /** Micro-app docked beside chat (Cap'n Web iframe + Accept gate). */
  microAppDefinitionId: string | null
  microAppDisplayName: string | null
  microAppPanelOpen: boolean
  setMicroAppPanelOpen: (v: boolean) => void
  closeMicroAppPanel: () => void

  // artifact + version switcher
  sessionArtifacts: SessionArtifact[]
  selectedArtifactId: string | null
  selectedVersion: number | null
  viewingVersion: string | null
  setSelectedVersion: (v: number | null) => void
  setViewingVersion: (v: string | null) => void
  openDraft: (type: string, artifactId: string | null) => void
  selectVersion: (artifact: SessionArtifact, version: PanelArtifactVersion) => void

  // publishing
  publishing: boolean
  setPublishing: (v: boolean) => void

  // resize / view controls
  panelWidthPct: number
  isDragging: boolean
  handleDragStart: (e: React.PointerEvent) => void
  previewDevice: 'desktop' | 'mobile'
  setPreviewDevice: (d: 'desktop' | 'mobile') => void
  siteView: 'preview' | 'code'
  setSiteView: (updater: ((v: 'preview' | 'code') => 'preview' | 'code') | 'preview' | 'code') => void
  openSiteInNewTab: () => void
  downloadSiteHtml: () => void
}

export function ChatWorkspace(props: ChatWorkspaceProps) {
  const {
    chatRef, chatService, userName, liveSessionId, apiBaseUrl, token, devMode, setDevMode, showDevToggle,
    onHasMessagesChange, onMessagesMirror, onStreamingChange, sessionResuming,
    onSessionCreated, fetchVoiceToken, uploadFile, onStreamingAssistantContent, onMcpApp, onMarkdownArtifact, onAppArtifactOpen, onMicroAppOpen, onLocationRequest,
    onSiteBuilderPlan, onSiteBuilderGenerating, onSiteBuilderCodeChunk, onSiteBuilderPreview, onSiteBuilderPublished,
    draftBanner, setDraftBanner,
    mcpPanelData, mcpPanelOpen, setMcpPanelOpen, mcpContainerRef, siteArtifacts, siteBuilderOpen, setSiteBuilderOpen,
    markdownArtifact, markdownArtifactOpen, setMarkdownArtifactOpen, setMarkdownArtifact,
    artifactsMgmtEnabled, artifactsListOpen, setArtifactsListOpen, previewArtifact, setPreviewArtifact,
    downloadArtifactVersion, onDeleteArtifact,
    microAppDefinitionId, microAppDisplayName, microAppPanelOpen, setMicroAppPanelOpen, closeMicroAppPanel,
    sessionArtifacts, selectedArtifactId, selectedVersion, viewingVersion, setSelectedVersion, setViewingVersion,
    openDraft, selectVersion,
    publishing, setPublishing,
    panelWidthPct, isDragging, handleDragStart,
    previewDevice, setPreviewDevice, siteView, setSiteView, openSiteInNewTab, downloadSiteHtml,
  } = props

  const shell = useRosShell()
  const {authFetch, reportUnavailable} = useRosFeatures()
  const [panelFullscreen, setPanelFullscreen] = useState(false)
  const activeSession = shell.sessions.find(s => s.id === liveSessionId)
  const sessionTitle = activeSession?.intentSummary ?? null
  const activeAgentSlug = activeSession?.activeAgent ?? 'discovery'
  const persistedModelOverride = activeSession?.tags?.model_override
  const [initialModelLabel, setInitialModelLabel] = useState<string | undefined>(undefined)
  // The dev-mode model picker's options — same catalog Settings uses, fetched once alongside
  // initialModelLabel below (both dev-mode-gated, both come from the same /models response).
  const [modelCatalog, setModelCatalog] = useState<CatalogModelRow[]>([])

  // Model identity and switching are advanced diagnostics: regular users do not fetch or render
  // the catalog. The server snapshot owns authorization; a revoked grant closes dev mode locally.
  useEffect(() => {
    if (!devMode || !showDevToggle) {
      setInitialModelLabel(undefined)
      setModelCatalog([])
      return
    }
    const controller = new AbortController()
    const headers: Record<string, string> = {}
    if (token) headers.Authorization = `Bearer ${token}`
    void Promise.all([
      authFetch(`${apiBaseUrl}/api/agent-builder/agents`, {credentials: 'include', headers, signal: controller.signal}),
      authFetch(`${apiBaseUrl}/api/agent-builder/models`, {credentials: 'include', headers, signal: controller.signal}),
    ]).then(async ([agentsResponse, modelsResponse]) => {
      if (agentsResponse.status === 403 || modelsResponse.status === 403) {
        setDevMode(false)
        setInitialModelLabel(undefined)
        setModelCatalog([])
        reportUnavailable()
        return
      }
      if (!agentsResponse.ok || !modelsResponse.ok) throw new Error('Model diagnostics are unavailable')
      const [agentsPayload, modelsPayload] = await Promise.all([agentsResponse.json(), modelsResponse.json()])
      const agents = (agentsPayload?.agents as AgentModelRow[] | undefined) ?? []
      const models = (modelsPayload?.models as CatalogModelRow[] | undefined) ?? []
      const agent = agents.find(item => item.slug === activeAgentSlug)
      const selected = models.find(model => model.spec === persistedModelOverride)
        ?? models.find(model => model.provider === agent?.modelProvider && model.modelId === agent?.modelId)
      setModelCatalog(models)
      setInitialModelLabel(selected ? `Model: ${selected.label}` : undefined)
    }).catch(error => {
      if ((error as Error).name !== 'AbortError') console.error('[Ros] Failed to load debug model catalog', error)
    })
    return () => controller.abort()
  }, [apiBaseUrl, token, activeAgentSlug, persistedModelOverride, devMode, showDevToggle, authFetch, reportUnavailable, setDevMode])

  // Dev-mode-only: set this session's model override. Backed by the generic session-tag endpoint
  // (same one used for client/research_domain tags) rather than a bespoke route — the override IS
  // just a tag (tagType 'model_override'), stored as a catalog spec string ("provider:model-id").
  const setModelOverride = useCallback(async (sessionId: string, spec: string) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await authFetch(`${apiBaseUrl}/api/r3/session-tag`, {
      method: 'POST', credentials: 'include', headers,
      body: JSON.stringify({sessionId, tagType: 'model_override', tagValue: spec}),
    })
    const payload = await response.json().catch(() => null) as {error?: string} | null
    if (response.status === 403) {
      setDevMode(false)
      setInitialModelLabel(undefined)
      setModelCatalog([])
      reportUnavailable()
    }
    if (!response.ok) throw new Error(payload?.error || 'The model override could not be saved')
  }, [apiBaseUrl, token, authFetch, reportUnavailable, setDevMode])

  const crosstabsFilterEnabled = useFeatureFlag('crosstabs-filter')
  const mcpFeatureFlags = useMemo(() => ({ 'crosstabs-filter': crosstabsFilterEnabled }), [crosstabsFilterEnabled])
  const markdownService = useMemo(() => new MarkdownArtifactService(apiBaseUrl, token), [apiBaseUrl, token])
  const downloadAppArtifactBuilder = useCallback(async (data: AppArtifactBuilderData) => {
    const {blob, fileName} = await fetchAppArtifactBuilderSkillZip(
      apiBaseUrl,
      data.fileName,
      authFetch as typeof fetch,
    )
    const objectUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
  }, [apiBaseUrl, authFetch])

  // Whether the conversation has any messages — drives the ChatHeader visibility (NOT liveSessionId,
  // which is undefined during a fresh chat's first turn, so gating on it hid the header mid-chat).
  const [hasMessages, setHasMessages] = useState(false)
  // Live conversation, mirrored from R3Chat for the Export action (ported from Studio Reachy chat).
  const [chatMessages, setChatMessages] = useState<ConversationMessage[]>([])
  // Anchor for the site-builder panel's "⋯" overflow menu (secondary actions live here).
  const [siteMenuAnchor, setSiteMenuAnchor] = useState<null | HTMLElement>(null)
  const siteMenuOpen = Boolean(siteMenuAnchor)
  const [revisionHistoryOpen, setRevisionHistoryOpen] = useState(false)
  const composerDraftStore = useMemo(() => new WebsiteComposerDraftStore(), [])
  const contextualComposerDraftStore = useMemo(() => new WebsiteComposerDraftStore(), [])
  const [selectMode, setSelectMode] = useState(false)
  const [selectMenuAnchor, setSelectMenuAnchor] = useState<null | HTMLElement>(null)
  const [selectComposerPlacement, setSelectComposerPlacement] = useState<SelectComposerPlacement>('both')
  const [websiteTargets, setWebsiteTargets] = useState<PendingWebsiteTarget[]>([])
  const [websiteApplyMode, setWebsiteApplyMode] = useState<WebsiteApplyMode>('review')
  const [contextualComposerOpen, setContextualComposerOpen] = useState(false)
  const [websiteAnchorRect, setWebsiteAnchorRect] = useState<WebsiteAnchorRect | null>(null)
  const [previewContainerSize, setPreviewContainerSize] = useState({ width: 760, height: 600 })
  const [websitePendingFiles, setWebsitePendingFiles] = useState<Array<{name: string; id?: string; uploading: boolean; scope: 'main' | 'contextual'}>>([])
  const [websiteVoice, setWebsiteVoice] = useState<{status: 'idle' | 'connecting' | 'recording' | 'stopping'; transcript: string; destination: 'main' | 'external'}>({status: 'idle', transcript: '', destination: 'main'})
  const [pendingWebsiteChangeSet, setPendingWebsiteChangeSet] = useState<WebsiteChangeSetSummary | null>(null)
  const [reviewBusyAction, setReviewBusyAction] = useState<'approve' | 'reject' | null>(null)
  const [refiningChangeSetId, setRefiningChangeSetId] = useState<string | undefined>()
  const [websiteActionError, setWebsiteActionError] = useState<string | null>(null)
  const [publishedFeedbackUrl, setPublishedFeedbackUrl] = useState<string | null>(null)
  const [publishedLinkCopied, setPublishedLinkCopied] = useState(false)
  const previewContainerRef = useRef<HTMLDivElement>(null)
  const nextTargetOrdinal = useRef(1)
  const websiteTargetOrdinals = useRef(new Map<string, number>())
  const websiteBuilderService = useMemo(() => new WebsiteBuilderService(apiBaseUrl, token), [apiBaseUrl, token])
  const revisionWebsiteBuilderService = useMemo(() => new RevisionWebsiteBuilderService(apiBaseUrl, token), [apiBaseUrl, token])
  const approveWebsitePlan = useCallback(
    (sessionId: string) => websiteBuilderService.approvePlan(sessionId),
    [websiteBuilderService],
  )
  const handleRevisionHistoryFullscreen = useCallback((fullscreen: boolean) => {
    setPanelFullscreen(() => fullscreen)
  }, [])

  const siteRevision = siteArtifacts?.revision ?? 0
  const currentPublishedUrl = useMemo(() => stablePublishedUrl(siteArtifacts?.publishedUrl), [siteArtifacts?.publishedUrl])
  useEffect(() => {
    setSelectMode(false)
    setSelectMenuAnchor(null)
    setWebsiteTargets([])
    setContextualComposerOpen(false)
    setWebsiteAnchorRect(null)
    const pendingChangeSet = siteArtifacts?.pendingChangeSet
    const restoredChangeSet: WebsiteChangeSetSummary | null = pendingChangeSet
      ? {
          ...pendingChangeSet,
          affectedTargets: pendingChangeSet.affectedTargets.map(target => ({
            ...target,
            label: target.label || target.targetId,
          })),
        }
      : null
    setPendingWebsiteChangeSet(restoredChangeSet)
    if (restoredChangeSet) setPanelFullscreen(() => true)
    setRefiningChangeSetId(undefined)
    setPublishedFeedbackUrl(null)
    setPublishedLinkCopied(false)
    nextTargetOrdinal.current = 1
    websiteTargetOrdinals.current.clear()
  }, [siteArtifacts?.siteId, siteRevision, siteArtifacts?.pendingChangeSet?.changeSetId, viewingVersion, setPanelFullscreen])

  const copyPublishedLink = useCallback(async (url: string) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard is unavailable')
      await navigator.clipboard.writeText(url)
      setPublishedLinkCopied(true)
      setWebsiteActionError(null)
    } catch {
      setWebsiteActionError('The published link could not be copied. Open it and copy the URL from your browser.')
    }
  }, [])

  const handleWebsitePublishedSuccess = useCallback((url: string, siteId: string | number) => {
    const liveUrl = stablePublishedUrl(url) ?? url
    onSiteBuilderPublished(liveUrl, siteId)
    setPublishedFeedbackUrl(liveUrl)
    setPublishedLinkCopied(false)
    setWebsiteActionError(null)
  }, [onSiteBuilderPublished])

  useEffect(() => {
    setRevisionHistoryOpen(false)
  }, [siteArtifacts?.siteId, viewingVersion])

  useEffect(() => {
    const element = previewContainerRef.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const update = () => setPreviewContainerSize({width: element.clientWidth, height: element.clientHeight})
    update()
    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => observer.disconnect()
  }, [siteBuilderOpen, siteView, panelFullscreen])

  const removeWebsiteTarget = useCallback((targetId: string) => {
    setWebsiteTargets(current => current.filter(target => target.targetId !== targetId))
  }, [])

  const handleWebsiteBridgeMessage = useCallback((message: WebsitePreviewBridgeMessage) => {
    if (message.type === 'select-mode-exited') {
      setSelectMode(false)
      return
    }
    if (message.type === 'target-deselected') {
      removeWebsiteTarget(message.targetId)
      return
    }
    if (message.type === 'target-rect-updated') {
      setWebsiteAnchorRect(message.anchorRect)
      return
    }
    if (message.type !== 'target-selected') return
    setWebsiteAnchorRect(message.anchorRect)
    setContextualComposerOpen(selectComposerPlacement !== 'ros')
    let ordinal = websiteTargetOrdinals.current.get(message.targetId)
    if (ordinal === undefined) {
      ordinal = nextTargetOrdinal.current++
      websiteTargetOrdinals.current.set(message.targetId, ordinal)
    }
    setWebsiteTargets(current => {
      if (current.some(target => target.targetId === message.targetId)) return current
      return [...current, {
        targetId: message.targetId,
        siteId: message.siteId,
        revision: message.revision,
        label: message.label,
        ordinal,
      }]
    })
  }, [removeWebsiteTarget, selectComposerPlacement])

  const handleWebsiteChangeSetReady = useCallback((data: WebsiteChangeSetReadyData) => {
    const labels = new Map(websiteTargets.map(target => [target.targetId, target.label]))
    setPendingWebsiteChangeSet({
      ...data,
      affectedTargets: data.affectedTargets.map(target => ({...target, label: target.label || labels.get(target.targetId) || target.targetId})),
    })
    setSelectMode(false)
    setSiteBuilderOpen(true)
    setPanelFullscreen(() => true)
  }, [websiteTargets, setSiteBuilderOpen, setPanelFullscreen])

  const approvePendingWebsiteChangeSet = useCallback(async () => {
    if (!liveSessionId || !pendingWebsiteChangeSet) return
    setReviewBusyAction('approve')
    setWebsiteActionError(null)
    try {
      const result = await websiteBuilderService.approveChangeSet(liveSessionId, pendingWebsiteChangeSet.changeSetId)
      const appliedHtml = result.renderHtml ?? result.html
      if (result.status !== 'applied' || !appliedHtml) throw new Error('The proposal was not applied. Refresh the website and try again.')
      onSiteBuilderPreview(appliedHtml, result.siteId || pendingWebsiteChangeSet.siteId, result.appliedRevision ?? result.revision, result.html ?? undefined)
      setPendingWebsiteChangeSet(null)
      setRefiningChangeSetId(undefined)
      setWebsiteTargets([])
      websiteTargetOrdinals.current.clear()
      nextTargetOrdinal.current = 1
      setPanelFullscreen(() => false)
    } catch (error) {
      setWebsiteActionError(error instanceof Error ? error.message : 'The proposal could not be approved.')
    } finally {
      setReviewBusyAction(null)
    }
  }, [liveSessionId, pendingWebsiteChangeSet, websiteBuilderService, onSiteBuilderPreview, setPanelFullscreen])

  const rejectPendingWebsiteChangeSet = useCallback(async () => {
    if (!liveSessionId || !pendingWebsiteChangeSet) return
    setReviewBusyAction('reject')
    setWebsiteActionError(null)
    try {
      await websiteBuilderService.rejectChangeSet(liveSessionId, pendingWebsiteChangeSet.changeSetId)
      setPendingWebsiteChangeSet(null)
      setRefiningChangeSetId(undefined)
      setWebsiteTargets([])
      websiteTargetOrdinals.current.clear()
      nextTargetOrdinal.current = 1
      if (siteArtifacts?.htmlContent) {
        onSiteBuilderPreview(siteArtifacts.renderHtml ?? siteArtifacts.htmlContent, siteArtifacts.siteId, siteRevision, siteArtifacts.htmlContent)
      }
      setPanelFullscreen(() => false)
    } catch (error) {
      setWebsiteActionError(error instanceof Error ? error.message : 'The proposal could not be rejected.')
    } finally {
      setReviewBusyAction(null)
    }
  }, [liveSessionId, pendingWebsiteChangeSet, websiteBuilderService, siteArtifacts, siteRevision, onSiteBuilderPreview, setPanelFullscreen])

  const refinePendingWebsiteChangeSet = useCallback(async () => {
    if (!liveSessionId || !pendingWebsiteChangeSet) return
    try {
      await websiteBuilderService.reopenChangeSet(liveSessionId, pendingWebsiteChangeSet.changeSetId)
      setRefiningChangeSetId(pendingWebsiteChangeSet.changeSetId)
      setPendingWebsiteChangeSet(null)
      setContextualComposerOpen(true)
      setPanelFullscreen(() => false)
    } catch (error) {
      setWebsiteActionError(error instanceof Error ? error.message : 'The proposal could not be reopened.')
    }
  }, [liveSessionId, pendingWebsiteChangeSet, websiteBuilderService, setPanelFullscreen])

  const publishCurrentWebsiteRevision = useCallback(async () => {
    if (!liveSessionId || !siteArtifacts || publishing) return
    setPublishing(true)
    setWebsiteActionError(null)
    setPublishedFeedbackUrl(null)
    setPublishedLinkCopied(false)
    try {
      const published = await websiteBuilderService.publishRevision(liveSessionId, siteRevision)
      handleWebsitePublishedSuccess(published.liveUrl, published.siteId)
      setDraftBanner(null)
    } catch (error) {
      setWebsiteActionError(error instanceof Error ? error.message : 'Publishing failed. Your version is unchanged.')
    } finally {
      setPublishing(false)
    }
  }, [liveSessionId, siteArtifacts, publishing, setPublishing, websiteBuilderService, siteRevision, handleWebsitePublishedSuccess, setDraftBanner])
  // Title from Accept/meta load so the panel chrome can show displayName after meta resolves.
  const [microAppTitle, setMicroAppTitle] = useState<string | null>(null)
  useEffect(() => { setMicroAppTitle(null) }, [microAppDefinitionId])

  // R3Chat mirrors messages from an effect that depends on this callback. Keeping the identity
  // stable prevents that effect and these state updates from sustaining a parent/child render loop.
  const handleMessagesChange = useChatMessageChangeHandler(
    setHasMessages,
    onHasMessagesChange,
    setChatMessages,
    onMessagesMirror,
  )

  const activePanelType = microAppPanelOpen && microAppDefinitionId ? 'micro_app'
    : mcpPanelOpen && mcpPanelData ? 'mcp_app'
    : siteBuilderOpen && siteArtifacts ? 'site_builder'
    : markdownArtifactOpen && markdownArtifact ? 'markdown_artifact'
    : previewArtifact ? 'artifact_preview'
    : artifactsListOpen ? 'artifacts_list'
    : null
  const rightPanelOpen = activePanelType !== null
  const rightPanelWidthPct = activePanelType === 'artifacts_list' ? 30 : panelWidthPct
  const chatWidthPct = rightPanelOpen ? 100 - rightPanelWidthPct : 100
  const websiteIsEditable = hasEditableWebsiteSnapshot({
    panelActive: activePanelType === 'site_builder',
    viewingPublishedVersion: selectedVersion !== null,
    viewingHistoricalRevision: !!viewingVersion,
    htmlContent: siteArtifacts?.htmlContent,
  })
  const contextualWebsiteEditContext = websiteIsEditable && siteArtifacts
    ? toWebsiteEditContext({
        siteId: String(siteArtifacts.siteId),
        revision: siteRevision,
        applyMode: websiteApplyMode,
        targets: websiteTargets,
        pendingChangeSetId: refiningChangeSetId ?? pendingWebsiteChangeSet?.changeSetId,
      })
    : undefined
  const websiteEditContext = websiteIsEditable && siteArtifacts
    ? toWebsiteEditContext({
        siteId: String(siteArtifacts.siteId),
        revision: siteRevision,
        applyMode: websiteApplyMode,
        targets: selectComposerPlacement === 'canvas' ? [] : websiteTargets,
        pendingChangeSetId: refiningChangeSetId ?? pendingWebsiteChangeSet?.changeSetId,
      })
    : undefined
  const mainWebsiteTargetReferences = selectComposerPlacement === 'canvas'
    ? []
    : websiteTargets.map(({ordinal, targetId, label}) => ({ordinal, targetId, label}))
  const contextualWebsiteTargetReferences = websiteTargets.map(({ordinal, targetId, label}) => ({ordinal, targetId, label}))
  const activeContextualDraftStore = selectComposerPlacement === 'both' ? composerDraftStore : contextualComposerDraftStore
  const contextualPendingFileScope = selectComposerPlacement === 'both' ? 'main' : 'contextual'
  const contextualPendingFiles = websitePendingFiles.filter(file => file.scope === contextualPendingFileScope)
  const websiteTargetChips = websiteIsEditable && selectComposerPlacement !== 'canvas' && websiteTargets.length
    ? <TargetChipBar targets={websiteTargets} onRemove={removeWebsiteTarget} />
    : undefined

  const selectedArtifact = sessionArtifacts.find(a => a.id === selectedArtifactId) ?? null
  const panelVersions = selectedArtifact?.versions ?? []
  const panelArtifactProtected = !!selectedArtifact?.isProtected
  const currentRevisionPublishedVersion = panelVersions
    .filter(version => version.summary?.sourceRevision === siteRevision)
    .reduce<number | null>((latest, version) => latest === null || version.version > latest ? version.version : latest, null)

  const closeRightPanel = () => {
    setMcpPanelOpen(false)
    setSiteBuilderOpen(false)
    setMarkdownArtifactOpen(false)
    closeMicroAppPanel()
    setArtifactsListOpen(false)
    setPreviewArtifact(null)
    setPanelFullscreen(() => false)
  }

  const togglePanel = () => {
    if (rightPanelOpen) {
      closeRightPanel()
    } else if (microAppDefinitionId) {
      setMicroAppPanelOpen(true)
    } else if (artifactsMgmtEnabled && sessionArtifacts.length > 0) {
      setArtifactsListOpen(true)
    } else if (mcpPanelData) {
      setMcpPanelOpen(true)
    } else if (siteArtifacts) {
      setSiteBuilderOpen(true)
    } else if (markdownArtifact) {
      setMarkdownArtifactOpen(true)
    }
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
      {/* Chat column */}
      <Box sx={{
        width: `${chatWidthPct}%`,
        transition: isDragging ? 'none' : 'width 0.2s ease',
        display: panelFullscreen ? 'none' : 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}>
        {/* While a session's history is loading (hard refresh or rail click), cover the chat column
            with an opaque loading state — otherwise R3Chat's empty state shows for the duration of
            the fetch and reads as a lost conversation. R3Chat stays mounted underneath (it must
            never unmount — it owns the live stream). */}
        {sessionResuming && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 3,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5,
            bgcolor: 'background.default',
          }}>
            <CircularProgress size={28} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Loading conversation…</Typography>
          </Box>
        )}
        {/* Per-conversation header: title + ⋮ (rename/delete) on the left, the chat actions
            (export / panel / dev-mode) on the right — one row, one hairline. Shows once a chat
            is active. The actions used to live in R3Chat's own header bar (an extra divider);
            they're consolidated here now. */}
        <ChatHeader
          visible={hasMessages}
          actions={
            <>
              <ExportMenuButton
                disabled={!hasMessages}
                onExport={(fmt) => {
                  if (!chatMessages.length) return
                  const s = shell.sessions.find(x => x.id === liveSessionId)
                  void exportConversation(fmt, chatMessages, s?.intentSummary ?? s?.activeAgent, s?.startedAt, userName, liveSessionId)
                }}
              />
              <Tooltip title={rightPanelOpen ? 'Close panel' : 'Open panel'}><IconButton size="small" onClick={togglePanel} sx={{ color: rightPanelOpen ? 'primary.main' : 'text.secondary' }}><ViewSidebarIcon fontSize="small" /></IconButton></Tooltip>
              {showDevToggle && (
                <Tooltip title={devMode ? 'Dev mode ON — click to switch to clean mode' : 'Click to enable dev mode'}><IconButton size="small" onClick={() => setDevMode(!devMode)} sx={{ color: devMode ? 'secondary.main' : 'text.disabled' }}><BugReportIcon fontSize="small" /></IconButton></Tooltip>
              )}
            </>
          }
        />
        {/* Unpublished-changes banner — the working draft is ahead of the last published version */}
        {draftBanner && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.75,
            background: 'var(--warning-light, #fdf6e8)', borderBottom: '1px solid', borderColor: 'warning.light',
          }}>
            <Typography sx={{ fontSize: '0.78rem', color: 'warning.dark', fontWeight: 500 }}>
              {draftBanner.type === 'site_builder' && siteArtifacts
                ? `Version ${siteRevision + 1} has unpublished changes — publish to create published version ${(draftBanner.baseVersion ?? 0) + 1}.`
                : `Unpublished changes since published version ${draftBanner.baseVersion ?? '?'} — publish to create published version ${(draftBanner.baseVersion ?? 0) + 1}.`}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography
              onClick={() => {
                if (draftBanner.type === 'site_builder') {
                  void publishCurrentWebsiteRevision()
                  return
                }
                const noun = draftBanner.type === 'crosstabs' ? 'crosstabs deliverable' : draftBanner.type === 'site_builder' ? 'website' : 'survey'
                chatRef.current?.sendMessage(`Publish the ${noun} — I approve the current draft.`)
                setDraftBanner(null)
              }}
              sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'warning.dark', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              Publish
            </Typography>
            <Typography onClick={() => setDraftBanner(null)} sx={{ fontSize: '0.85rem', color: 'text.disabled', cursor: 'pointer' }}>✕</Typography>
          </Box>
        )}
        {/* R3Chat's root is height:100%; in this flex column (after the header/banner) that overflows
            and clips the bottom input. Wrap in a flex:1 + minHeight:0 box so it fills only the
            REMAINING space below the header and the input stays in view. */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <R3Chat
          ref={chatRef}
          userName={userName}
          chatService={chatService}
          onMessagesChange={handleMessagesChange}
          onSessionCreated={onSessionCreated}
          fetchVoiceToken={fetchVoiceToken}
          onStreamingAssistantContent={onStreamingAssistantContent}
          onStreamingChange={onStreamingChange}
          onMcpApp={onMcpApp}
          onMarkdownArtifactPanel={onMarkdownArtifact}
          onAppArtifactOpen={onAppArtifactOpen}
          onMicroAppOpen={onMicroAppOpen
            ? (data) => onMicroAppOpen(data.definitionId, data.displayName)
            : undefined}
          onLocationRequest={onLocationRequest}
          onDownloadAppArtifactBuilder={downloadAppArtifactBuilder}
          onSiteBuilderPlan={onSiteBuilderPlan}
          onSiteBuilderGenerating={onSiteBuilderGenerating}
          onSiteBuilderCodeChunk={onSiteBuilderCodeChunk}
          onSiteBuilderPreview={onSiteBuilderPreview}
          onSiteBuilderPublished={handleWebsitePublishedSuccess}
          onWebsiteChangeSetReady={handleWebsiteChangeSetReady}
          onApprovePlan={approveWebsitePlan}
          apiBaseUrl={apiBaseUrl}
          sessionId={liveSessionId}
          devMode={devMode}
          uploadFile={uploadFile}
          initialModelLabel={initialModelLabel}
          availableModels={modelCatalog}
          onSetModelOverride={setModelOverride}
          websiteEditContext={websiteEditContext}
          websiteTargetReferences={mainWebsiteTargetReferences}
          websiteTargetChips={websiteTargetChips}
          composerDraftStore={composerDraftStore}
          onPendingFilesChange={setWebsitePendingFiles}
          onVoiceStateChange={setWebsiteVoice}
        />
        </Box>
      </Box>

      {/* Drag handle */}
      {rightPanelOpen && !panelFullscreen && activePanelType !== 'artifacts_list' && (
        <Box
          onPointerDown={handleDragStart}
          sx={{
            width: 4, cursor: 'col-resize', background: isDragging ? 'primary.main' : 'divider',
            '&:hover': { background: 'primary.main' }, transition: 'background 0.15s', flexShrink: 0,
          }}
        />
      )}

      {/* Right panel — MCP App or Site Preview. */}
      {rightPanelOpen && (
        <Box sx={{
          width: `${rightPanelWidthPct}%`,
          transition: isDragging ? 'none' : 'width 0.2s ease',
          borderLeft: '1px solid', borderColor: 'divider',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          position: 'relative', // anchors the dev-mode Edit History sidebar below
          ...(panelFullscreen && {
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            zIndex: 10, borderLeft: 'none', bgcolor: 'background.paper',
          }),
        }}>
          {activePanelType === 'artifacts_list' ? (
            <ArtifactsPanel
              artifacts={sessionArtifacts}
              onSelectArtifact={artifact => {
                setArtifactsListOpen(false)
                if (artifact.type === 'website') {
                  setPreviewArtifact(null)
                  void openDraft(artifact.type, artifact.id)
                } else {
                  setPreviewArtifact(artifact)
                }
              }}
              onDownload={downloadArtifactVersion}
              onDelete={onDeleteArtifact}
              onClose={() => setArtifactsListOpen(false)}
            />
          ) : activePanelType === 'artifact_preview' && previewArtifact ? (
            <ArtifactPreviewPanel
              artifacts={[previewArtifact]}
              apiBaseUrl={apiBaseUrl}
              token={token}
              onDownload={downloadArtifactVersion}
              onClose={() => { setPreviewArtifact(null); setArtifactsListOpen(true) }}
            />
          ) : (
            <>
          {!pendingWebsiteChangeSet && <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', background: 'background.paper',
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {panelFullscreen && sessionTitle && (
                <Typography noWrap sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2 }}>{sessionTitle}</Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'primary.dark', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {activePanelType === 'micro_app'
                    ? (microAppTitle || microAppDisplayName || microAppDefinitionId || 'Micro App')
                    : activePanelType === 'markdown_artifact' ? 'Document'
                    : activePanelType === 'site_builder' ? 'Site Preview' :
                   mcpPanelData?.toolName === 'show_survey_review' ? 'Survey Review' :
                   mcpPanelData?.serverId === 'crosstabs' ? 'Crosstabs' : 'MCP App'}{viewingVersion && activePanelType !== 'micro_app' ? ` — ${viewingVersion} (published)` : ''}
                </Typography>
                {activePanelType === 'micro_app' && (
                  <Chip
                    size="small"
                    label="Editing in chat"
                    sx={{
                      height: 22,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      bgcolor: 'rgba(232, 93, 60, 0.12)',
                      color: '#E85D3C',
                      border: '1px solid',
                      borderColor: 'rgba(232, 93, 60, 0.35)',
                    }}
                  />
                )}
              </Box>
            </Box>

            {activePanelType !== 'micro_app' && sessionArtifacts.length > 1 && (
              <select
                value={selectedArtifactId ?? ''}
                onChange={e => {
                  const art = sessionArtifacts.find(a => a.id === e.target.value)
                  if (art) openDraft(art.type, art.id)
                }}
                style={{
                  fontSize: '0.68rem', color: 'var(--color-text, #3d3932)', border: '1px solid var(--color-border, #e8e4d8)',
                  borderRadius: 6, padding: '2px 6px', background: 'var(--color-bg-paper, #fff)', marginRight: 6, cursor: 'pointer', maxWidth: 160,
                }}
                title="Switch artifact"
              >
                {selectedArtifactId === null && <option value="" disabled>Select artifact</option>}
                {sessionArtifacts.map(a => (
                  <option key={a.id} value={a.id}>{artifactLabel(a)}</option>
                ))}
              </select>
            )}

            {(activePanelType === 'site_builder' || mcpPanelData?.serverId === 'crosstabs' || mcpPanelData?.toolName === 'show_survey_review') && (
              <select
                value={selectedVersion !== null ? String(selectedVersion) : 'draft'}
                onChange={e => {
                  if (e.target.value === 'draft') {
                    if (selectedArtifact) openDraft(selectedArtifact.type, selectedArtifact.id)
                    else { setSelectedVersion(null); setViewingVersion(null) }
                    return
                  }
                  const v = panelVersions.find(pv => pv.version === Number(e.target.value))
                  if (v && selectedArtifact) selectVersion(selectedArtifact, v)
                }}
                style={{
                  fontSize: '0.68rem', color: 'var(--color-text, #3d3932)', border: '1px solid var(--color-border, #e8e4d8)',
                  borderRadius: 6, padding: '2px 6px', background: 'var(--color-bg-paper, #fff)', marginRight: 6, cursor: 'pointer',
                }}
                title={activePanelType === 'site_builder' ? 'View the current working version or an immutable published version' : 'View the draft or a published version'}
              >
                <option value="draft">{activePanelType === 'site_builder' ? `Version ${siteRevision + 1} (current)` : 'Draft'}</option>
                {[...panelVersions].sort((a, b) => b.version - a.version).map(v => (
                  <option key={v.version} value={v.version}>{activePanelType === 'site_builder' ? `Published version ${v.version}` : `Version ${v.version}`}</option>
                ))}
              </select>
            )}

            {mcpPanelData?.serverId === 'crosstabs' && activePanelType === 'mcp_app' && selectedVersion === null && (
              <button
                type="button"
                disabled={publishing}
                onClick={() => {
                  setPublishing(true)
                  chatRef.current?.sendMessage('Publish the crosstabs deliverable — I approve the current draft.')
                }}
                title="Publish this crosstabs deliverable"
                style={{
                  fontSize: '0.68rem', color: '#fff', border: 'none', fontWeight: 600,
                  borderRadius: 6, padding: '4px 12px', marginRight: 6,
                  background: publishing ? 'var(--cyan-300, #7BD2E4)' : 'var(--cyan-600, #008FA8)',
                  cursor: publishing ? 'default' : 'pointer',
                }}
              >
                {publishing ? 'Publishing…' : 'Publish'}
              </button>
            )}

            {selectedArtifact && (
              <ArtifactProtectControl
                artifactId={selectedArtifact.id}
                initiallyProtected={panelArtifactProtected}
                apiBaseUrl={apiBaseUrl}
                token={token}
              />
            )}

            {activePanelType === 'site_builder' && websiteIsEditable && siteView === 'preview' && (
              <>
                <Tooltip title={`Select an element and write ${selectComposerPlacement === 'canvas' ? 'beside it' : selectComposerPlacement === 'ros' ? 'in Ros chat' : 'beside it or in Ros chat'}`}>
                  <ToggleButton
                    value="select"
                    selected={selectMode}
                    size="small"
                    onClick={event => setSelectMenuAnchor(event.currentTarget)}
                    aria-label="Choose where to write selected-element edits"
                    aria-haspopup="menu"
                    aria-expanded={Boolean(selectMenuAnchor)}
                    sx={{
                      height: 30, minWidth: 0, px: 1, mr: 0.75, gap: 0.5,
                      borderRadius: '7px !important', borderColor: 'divider',
                      color: 'text.secondary', fontSize: '0.68rem', fontWeight: 600,
                      textTransform: 'none', whiteSpace: 'nowrap',
                      '&.Mui-selected': {
                        color: 'primary.dark', bgcolor: 'action.selected', borderColor: 'primary.light',
                        '&:hover': { bgcolor: 'action.selected' },
                      },
                    }}
                  >
                    <TextFieldsIcon sx={{ fontSize: 16 }} />
                    Select
                    <ArrowDropDownIcon sx={{ fontSize: 16, ml: -0.25 }} />
                  </ToggleButton>
                </Tooltip>
                <Menu
                  anchorEl={selectMenuAnchor}
                  open={Boolean(selectMenuAnchor)}
                  onClose={() => setSelectMenuAnchor(null)}
                  MenuListProps={{ 'aria-label': 'Selected-element composer location' }}
                >
                  <Typography sx={{ px: 2, pt: 0.5, pb: 0.25, fontSize: '0.68rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Write selected edits
                  </Typography>
                  {([
                    ['canvas', 'Beside the element', 'Open the compact editor next to your selection.'],
                    ['ros', 'In Ros chat', 'Show the selected references only in the main composer.'],
                    ['both', 'Both', 'Use either composer with the same draft and references.'],
                  ] as const).map(([placement, primary, secondary]) => (
                    <MenuItem
                      key={placement}
                      selected={selectComposerPlacement === placement}
                      onClick={() => {
                        setSelectComposerPlacement(placement)
                        setSelectMode(true)
                        setContextualComposerOpen(placement !== 'ros' && websiteTargets.length > 0)
                        setSelectMenuAnchor(null)
                      }}
                    >
                      <ListItemIcon><Radio size="small" checked={selectComposerPlacement === placement} /></ListItemIcon>
                      <ListItemText primary={primary} secondary={secondary} secondaryTypographyProps={{ sx: { maxWidth: 270, whiteSpace: 'normal' } }} />
                    </MenuItem>
                  ))}
                  {selectMode && (
                    <>
                      <Divider />
                      <MenuItem onClick={() => { setSelectMode(false); setSelectMenuAnchor(null) }}>
                        <ListItemText inset primary="Stop selecting new elements" />
                      </MenuItem>
                    </>
                  )}
                </Menu>
                <Tooltip title={websiteApplyMode === 'review'
                  ? 'Review first: Ros shows Before and After so you can approve, reject, or refine before a version is created.'
                  : 'Auto-apply: Ros creates the version immediately after validation. You can still view and undo it.'}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={websiteApplyMode}
                    onChange={(_event, mode: WebsiteApplyMode | null) => { if (mode) setWebsiteApplyMode(mode) }}
                    aria-label="How Ros should apply website changes"
                    sx={{
                      height: 30, mr: 0.75,
                      '& .MuiToggleButton-root': {
                        px: 1, color: 'text.secondary', borderColor: 'divider',
                        fontSize: '0.65rem', fontWeight: 600, lineHeight: 1,
                        textTransform: 'none', whiteSpace: 'nowrap', boxShadow: 'none',
                      },
                      '& .MuiToggleButton-root.Mui-selected': {
                        color: 'primary.dark', bgcolor: 'action.selected',
                        '&:hover': { bgcolor: 'action.selected' },
                      },
                    }}
                  >
                    <ToggleButton value="review" aria-label="Review changes before applying">Review first</ToggleButton>
                    <ToggleButton value="auto" aria-label="Apply changes automatically">Auto-apply</ToggleButton>
                  </ToggleButtonGroup>
                </Tooltip>
              </>
            )}

            {/* Site-builder actions: primary Publish visible; secondary actions collapse into "⋯".
                Publishing is a typed revision action rather than a synthetic chat prompt. */}
            {activePanelType === 'site_builder' && siteArtifacts?.htmlContent && websiteIsEditable && (
              <>
                <button
                  type="button"
                  disabled={publishing || currentRevisionPublishedVersion !== null}
                  onClick={() => { void publishCurrentWebsiteRevision() }}
                  title={currentRevisionPublishedVersion !== null
                    ? `Version ${siteRevision + 1} is already published as published version ${currentRevisionPublishedVersion}. Make another change to publish again.`
                    : `Publish version ${siteRevision + 1}`}
                  style={{
                    fontSize: '0.68rem', color: '#fff', border: 'none', fontWeight: 600,
                    borderRadius: 6, padding: '4px 12px', marginRight: 6,
                    background: publishing || currentRevisionPublishedVersion !== null ? 'var(--cyan-300, #7BD2E4)' : 'var(--cyan-600, #008FA8)',
                    cursor: publishing || currentRevisionPublishedVersion !== null ? 'default' : 'pointer',
                  }}
                >
                  {publishing ? 'Publishing…' : currentRevisionPublishedVersion !== null ? `Published version ${currentRevisionPublishedVersion}` : 'Publish'}
                </button>

                <Tooltip title="More">
                  <IconButton size="small" onClick={e => setSiteMenuAnchor(e.currentTarget)} sx={{ color: 'text.secondary' }}>
                    <MoreHorizIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Menu anchorEl={siteMenuAnchor} open={siteMenuOpen} onClose={() => setSiteMenuAnchor(null)}>
                  <Typography sx={{ px: 2, py: 0.5, fontSize: '0.7rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5 }}>View</Typography>
                  <MenuItem onClick={() => { setSiteView('preview'); setPreviewDevice('desktop'); setSiteMenuAnchor(null) }}>
                    <ListItemIcon><Radio size="small" checked={siteView === 'preview' && previewDevice === 'desktop'} /></ListItemIcon>
                    <ListItemIcon><DesktopWindowsIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Desktop</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { setSiteView('preview'); setPreviewDevice('mobile'); setSiteMenuAnchor(null) }}>
                    <ListItemIcon><Radio size="small" checked={siteView === 'preview' && previewDevice === 'mobile'} /></ListItemIcon>
                    <ListItemIcon><PhoneIphoneIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Mobile</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { setSiteView('code'); setSiteMenuAnchor(null) }}>
                    <ListItemIcon><Radio size="small" checked={siteView === 'code'} /></ListItemIcon>
                    <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Code</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => { downloadSiteHtml(); setSiteMenuAnchor(null) }}>
                    <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Download HTML</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { openSiteInNewTab(); setSiteMenuAnchor(null) }}>
                    <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Open in new tab</ListItemText>
                  </MenuItem>
                  {currentPublishedUrl && (
                    <>
                      <Tooltip title={currentPublishedUrl} placement="left" arrow>
                        <MenuItem
                          component="a"
                          href={currentPublishedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setSiteMenuAnchor(null)}
                        >
                          <ListItemIcon><OpenInBrowserIcon fontSize="small" /></ListItemIcon>
                          <ListItemText
                            primary="Open published site"
                            secondary={currentPublishedUrl}
                            secondaryTypographyProps={{ sx: { maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                          />
                        </MenuItem>
                      </Tooltip>
                      <Tooltip title={`Copy ${currentPublishedUrl}`} placement="left" arrow>
                        <MenuItem onClick={() => { void copyPublishedLink(currentPublishedUrl); setSiteMenuAnchor(null) }}>
                          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                          <ListItemText>{publishedLinkCopied ? 'Published link copied' : 'Copy published link'}</ListItemText>
                        </MenuItem>
                      </Tooltip>
                    </>
                  )}
                  <Divider />
                  <MenuItem onClick={() => { setRevisionHistoryOpen(true); setSiteMenuAnchor(null) }}>
                    <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Version history</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}

            <Tooltip title={panelFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <IconButton size="small" onClick={() => setPanelFullscreen(() => !panelFullscreen)} sx={{ color: 'text.secondary' }}>
                {panelFullscreen ? <FullscreenExitIcon sx={{ fontSize: 18 }} /> : <FullscreenIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>

            <Box
              onClick={closeRightPanel}
              sx={{ cursor: 'pointer', fontSize: '1.1rem', color: 'text.secondary', ml: 0.5, '&:hover': { color: 'text.primary' } }}
            >
              ✕
            </Box>
          </Box>}

          {!pendingWebsiteChangeSet && activePanelType === 'site_builder' && siteArtifacts && liveSessionId && (
            <RevisionHistoryPanel
              open={revisionHistoryOpen}
              onClose={() => setRevisionHistoryOpen(false)}
              sessionId={liveSessionId}
              siteId={String(siteArtifacts.siteId)}
              currentRevision={siteRevision}
              service={revisionWebsiteBuilderService}
              device={previewDevice}
              devMode={devMode}
              publishing={publishing}
              onRequestFullscreen={handleRevisionHistoryFullscreen}
              onRevisionCreated={result => {
                if (result.renderHtml ?? result.html) onSiteBuilderPreview(result.renderHtml ?? result.html!, siteArtifacts.siteId, result.revision, result.html)
              }}
              onPublishCurrent={() => publishCurrentWebsiteRevision()}
            />
          )}

          <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            {websiteActionError && activePanelType === 'site_builder' && (
              <Alert severity="error" onClose={() => setWebsiteActionError(null)} sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 9 }}>
                {websiteActionError}
              </Alert>
            )}
            {publishedFeedbackUrl && !websiteActionError && activePanelType === 'site_builder' && (
              <PublishedWebsiteFeedback
                liveUrl={publishedFeedbackUrl}
                onDismiss={() => { setPublishedFeedbackUrl(null); setPublishedLinkCopied(false) }}
              />
            )}
            {pendingWebsiteChangeSet && activePanelType === 'site_builder' && (
              <Box sx={{ position: 'absolute', inset: 0, zIndex: 7, bgcolor: 'background.paper' }}>
                <ChangeReviewPanel
                  changeSet={pendingWebsiteChangeSet}
                  device={previewDevice}
                  narrow={!panelFullscreen && panelWidthPct < 65}
                  busyAction={reviewBusyAction}
                  onDeviceChange={setPreviewDevice}
                  onBack={() => setPanelFullscreen(() => false)}
                  onApprove={() => { void approvePendingWebsiteChangeSet() }}
                  onReject={() => { void rejectPendingWebsiteChangeSet() }}
                  onRefine={() => { void refinePendingWebsiteChangeSet() }}
                />
              </Box>
            )}
            {publishing && (
              <Box sx={{
                position: 'absolute', inset: 0, zIndex: 5,
                background: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(1.5px)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5,
              }}>
                <CircularProgress size={28} sx={{ color: 'primary.main' }} />
                <Typography sx={{ fontSize: '0.82rem', color: 'text.primary', fontWeight: 500 }}>Publishing your deliverable…</Typography>
              </Box>
            )}
            {activePanelType === 'micro_app' && microAppDefinitionId && (
              <MicroAppDock
                key={microAppDefinitionId}
                definitionId={microAppDefinitionId}
                displayName={microAppDisplayName ?? undefined}
                apiBaseUrl={apiBaseUrl}
                token={token}
                onMeta={(m) => setMicroAppTitle(m.displayName || m.id)}
              />
            )}
            {activePanelType === 'markdown_artifact' && markdownArtifact && (
              <MarkdownArtifactPanel
                state={markdownArtifact}
                service={markdownService}
                onChange={setMarkdownArtifact}
              />
            )}
            {activePanelType === 'mcp_app' && mcpPanelData && (
              <McpAppContainer
                ref={mcpContainerRef}
                {...mcpPanelData}
                apiBaseUrl={apiBaseUrl}
                // Prefer the session the WORKER stamped on the push — it is the authoritative one the
                // chat/agent used to populate the per-session DO. liveSessionId is only a fallback.
                sessionId={mcpPanelData.sessionId ?? liveSessionId}
                // Ros holds the Cognito token in sessionStorage (no auth cookie), so the panel's own
                // /api/mcp/tool calls must carry it as a Bearer header or the worker 401s them.
                authToken={token ?? undefined}
                featureFlags={mcpFeatureFlags}
                panelMode
                onMessage={(text) => chatRef.current?.sendMessage(text)}
              />
            )}
            {activePanelType === 'site_builder' && siteArtifacts?.htmlContent && siteView === 'preview' && (
              <Box ref={previewContainerRef} sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
                <WebsitePreviewFrame
                  html={siteArtifacts.renderHtml ?? siteArtifacts.htmlContent}
                  siteId={String(siteArtifacts.siteId)}
                  revision={siteRevision}
                  selectMode={selectMode}
                  readOnly={!websiteIsEditable}
                  device={previewDevice}
                  selectedTargets={websiteTargets}
                  onBridgeMessage={handleWebsiteBridgeMessage}
                />
                {websiteIsEditable && selectComposerPlacement !== 'ros' && contextualComposerOpen && websiteTargets.length > 0 && (
                  <ConnectedContextualWebsiteComposer
                    store={activeContextualDraftStore}
                    targets={websiteTargets}
                    applyMode={websiteApplyMode}
                    anchorRect={websiteAnchorRect}
                    containerSize={previewContainerSize}
                    narrow={previewDevice === 'mobile' || previewContainerSize.width < 620}
                    disabled={contextualPendingFiles.some(file => file.uploading)}
                    runningLabel={contextualPendingFiles.some(file => file.uploading) ? 'Uploading media…' : undefined}
                    pendingFileNames={contextualPendingFiles.map(file => file.name)}
                    voiceStatus={selectComposerPlacement === 'both' || websiteVoice.destination === 'external' ? websiteVoice.status : 'idle'}
                    voiceTranscript={selectComposerPlacement === 'both' || websiteVoice.destination === 'external' ? websiteVoice.transcript : ''}
                    onRemoveTarget={removeWebsiteTarget}
                    onApplyModeChange={setWebsiteApplyMode}
                    onAttach={files => chatRef.current?.attachFiles(files, {scope: contextualPendingFileScope})}
                    onVoice={() => chatRef.current?.startVoice({draftStore: activeContextualDraftStore})}
                    onVoiceAccept={() => chatRef.current?.acceptVoice({draftStore: activeContextualDraftStore})}
                    onVoiceCancel={() => chatRef.current?.cancelVoice()}
                    onSend={() => chatRef.current?.sendMessage(activeContextualDraftStore.getSnapshot(), {
                      websiteEditContext: contextualWebsiteEditContext,
                      websiteTargetReferences: contextualWebsiteTargetReferences,
                      draftStore: activeContextualDraftStore,
                      pendingFileScope: contextualPendingFileScope,
                    })}
                    onClose={() => setContextualComposerOpen(false)}
                  />
                )}
              </Box>
            )}
            {activePanelType === 'site_builder' && siteArtifacts?.htmlContent && siteView === 'code' && (
              <Box component="pre" sx={{
                width: '100%', height: '100%', overflow: 'auto', m: 0,
                p: 2, boxSizing: 'border-box', background: '#1e1e1e', color: '#d4d4d4',
                fontFamily: "'SF Mono', Menlo, Consolas, monospace", fontSize: '0.72rem',
                lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              }}>
                {siteArtifacts.htmlContent}
              </Box>
            )}
            {activePanelType === 'site_builder' && !siteArtifacts?.htmlContent && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary', fontSize: '0.85rem' }}>
                Building site...
              </Box>
            )}
          </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  )
}

type ConnectedContextualWebsiteComposerProps = Omit<React.ComponentProps<typeof ContextualWebsiteComposer>, 'value' | 'onChange'> & {
  store: WebsiteComposerDraftStore
}

/** Subscribe only the floating composer to keystrokes. ChatWorkspace and the message thread remain
 * outside this update path while both composer surfaces still share one exact draft. */
function ConnectedContextualWebsiteComposer({store, ...props}: ConnectedContextualWebsiteComposerProps) {
  const value = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)
  return <ContextualWebsiteComposer {...props} value={value} onChange={store.setValue} />
}

// Export-conversation button + format menu (Markdown / Word / Plain text). Ported from the Studio
// Reachy chat; disabled until the conversation has messages.
function ExportMenuButton({ disabled, onExport }: { disabled: boolean; onExport: (fmt: ExportFormat) => void }) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const choose = (fmt: ExportFormat) => { setAnchor(null); onExport(fmt) }
  return (
    <>
      <Tooltip title={disabled ? 'Export (no messages yet)' : 'Export conversation'}>
        <span>
          <IconButton size="small" disabled={disabled} onClick={e => setAnchor(e.currentTarget)} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => choose('md')} dense><ListItemIcon><ArticleIcon fontSize="small" /></ListItemIcon><ListItemText>Markdown (.md)</ListItemText></MenuItem>
        <MenuItem onClick={() => choose('docx')} dense><ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon><ListItemText>Word (.docx)</ListItemText></MenuItem>
        <MenuItem onClick={() => choose('txt')} dense><ListItemIcon><TextFieldsIcon fontSize="small" /></ListItemIcon><ListItemText>Plain text (.txt)</ListItemText></MenuItem>
      </Menu>
    </>
  )
}
